import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { LogOut, Settings, Loader2, MonitorCheck, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '../../api/axiosConfig';
import usePosStore from '../../stores/posStore';
import { useShiftSessionStore } from '../../features/pos/stores/shiftSessionStore';
import CloseShiftModal from './CloseShiftModal';

const posQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

// ── Inline session-state views (full-screen, outside the POS chrome) ──────────

function FullScreenSpinner() {
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-500">
      <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
      <p className="text-sm font-medium">Checking shift status…</p>
    </div>
  );
}

function ResumeCard() {
  const {
    activeSession,
    setActiveSession,
    clearSession,
    checkActiveSession,
  } = useShiftSessionStore();

  const [confirming, setConfirming] = useState(false);
  const [terminating, setTerminating] = useState(false);

  const session = activeSession;
  if (!session) return null;

  const fmt = (c) =>
    `₱ ${(c / 100).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;

  async function handleForceClose() {
    setTerminating(true);
    try {
      await api.post(`/pos/shift-session/${session._id}/force-close`, {
        forceCloseReason: 'Manually terminated on POS load',
      });
      clearSession();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to terminate session.');
    } finally {
      setTerminating(false);
      setConfirming(false);
    }
  }

  if (confirming) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <h2 className="text-lg font-semibold">Terminate Shift?</h2>
          </div>
          <p className="text-sm text-gray-600">
            This will force-close session <strong>{session.sessionNo}</strong>.
            Any unsettled orders will remain open. This action is logged.
          </p>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleForceClose}
              disabled={terminating}
              className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {terminating ? 'Terminating…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <MonitorCheck className="w-6 h-6 text-green-600 shrink-0" />
          <h1 className="text-xl font-semibold text-gray-900">Active Shift Found</h1>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
          {[
            ['Session #',    session.sessionNo],
            ['Cashier',      session.cashierName],
            ['Terminal',     session.terminalId],
            ['Opened',       formatDistanceToNow(new Date(session.openedAt), { addSuffix: true })],
            ['Opening Cash', fmt(session.openingCash)],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-2">
              <span className="text-gray-500">{label}</span>
              <span className="font-medium text-gray-800 text-right">{value}</span>
            </div>
          ))}
        </div>

        {/* Continue: sets session as active, PIN login provides the POS JWT */}
        <button
          type="button"
          onClick={() => setActiveSession(session)}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm mb-3 transition-colors"
        >
          Continue Shift
        </button>

        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="w-full py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors"
        >
          Terminate &amp; Start New
        </button>

        <p className="text-center text-xs text-gray-400 mt-4">
          You'll verify your PIN to re-authorise the POS terminal.
        </p>
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

export default function PosLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { posToken, posStaff, activeShift, restaurantName } = usePosStore();
  const {
    sessionStatus,
    activeSession,
    initTerminalId,
    checkActiveSession,
  } = useShiftSessionStore();

  const [showCloseShift, setShowCloseShift] = useState(false);

  // Initialise terminal ID and check for an existing open session on every mount.
  useEffect(() => {
    initTerminalId();
    checkActiveSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isShiftStart =
    location.pathname.endsWith('shift-start') || location.pathname === '/pos';

  // Redirect guard — waits for the session check to finish before deciding.
  useEffect(() => {
    // Still checking — do nothing yet.
    if (sessionStatus === 'checking') return;

    // Already has a valid POS JWT — nothing to do.
    if (posToken) return;

    // Already on the shift-start page — let it render.
    if (isShiftStart) return;

    // Active session found on server — render the inline resume card (see below).
    if (sessionStatus === 'resuming') return;

    // Session was just resumed (user clicked "Continue") but still needs
    // PIN re-auth to get a new POS JWT — send to shift-start.
    navigate('/pos/shift-start', { replace: true });
  }, [posToken, isShiftStart, sessionStatus, navigate]);

  // ── Pre-auth full-screen states ─────────────────────────────────────────
  if (!posToken) {
    if (sessionStatus === 'checking')  return <FullScreenSpinner />;
    if (sessionStatus === 'resuming')  return <ResumeCard />;
    // No token and not on shift-start: redirect is pending (one-frame delay) or
    // user navigated back. Show spinner so POS chrome never renders without a token.
    if (!isShiftStart) return <FullScreenSpinner />;
  }

  // ── Normal POS chrome ────────────────────────────────────────────────────
  return (
    <QueryClientProvider client={posQueryClient}>
      <div className="h-screen flex flex-col bg-gray-100">
        <header className="bg-gray-900 text-white px-6 py-3 flex items-center justify-between shadow">
          <div className="flex items-center gap-3">
            <span className="font-bold text-lg">{restaurantName || 'Restaurant POS'}</span>
            {activeShift && (
              <span className="text-xs bg-green-600 px-2 py-0.5 rounded-full">
                Shift active
              </span>
            )}
          </div>
          {posStaff && posToken && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-300">
                {posStaff.name} ({posStaff.role})
              </span>
              <Link
                to="/pos/settings/printer"
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
                title="Printer Settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
              <button
                onClick={() => setShowCloseShift(true)}
                className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300"
              >
                <LogOut className="h-4 w-4" />
                End Shift
              </button>
            </div>
          )}
        </header>
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
      {showCloseShift && (
        <CloseShiftModal
          onClose={() => setShowCloseShift(false)}
          onClosed={() => {
            setShowCloseShift(false);
            navigate('/pos/shift-start', { replace: true });
          }}
        />
      )}
    </QueryClientProvider>
  );
}
